/**
 * CODESMITH: MCP tool display utilities.
 *
 * Extracts human-readable metadata from MCP tool call events and builds
 * display labels for the session UI. Metadata is injected by the OpenCode
 * session processor (on tool_start) and prompt wrapper (on tool_end) via
 * state.metadata fields: mcpServerName, mcpToolName, mcpToolTitle,
 * mcpProviderId, mcpServerUrl. The serverName is the
 * user's display_name for the MCP server, passed from the backend
 * through codesmith to OpenCode.
 *
 * When structured metadata is unavailable (legacy tool parts), falls back
 * to regex parsing of the session-prefixed registration name.
 */
import { inferProviderFromHost } from "@/components/icons/McpProviderIcon";
import { MCP_PROVIDERS } from "@/components/icons/mcpProviders";

/**
 * Per-session MCP tool prefix shape: `s-{shortSessionId}_{server}_{tool}`.
 * `tool_event_relay` strips `workspace-tools_` already; user MCP tools
 * arrive with this prefix intact.
 */
const MCP_SESSION_TOOL_PREFIX_RE = /^s-[0-9a-f]{8,12}_(.+)$/;
// Codesmith-injected first-party servers, keyed by registration name.
// Transcripts disagree on this server's identity across eras: parts
// persisted before the rename to "Blacksmith Browser" carry
// mcpServerName "Browser", and metadata-less parts (e.g. orphan-cleanup
// tool_ends emitted on interrupted turns) only have the raw registration
// name. The sidebar MCP widget dedupes by server name, so every
// agent-browser part canonicalizes to this single label and provider id;
// otherwise a session spanning eras renders duplicate rows for the same
// integration ("Browser" next to "Blacksmith Browser").
const INJECTED_SERVERS: Record<
  string,
  { serverName: string; providerId: string }
> = {
  "agent-browser": {
    serverName: "Blacksmith Browser",
    providerId: "blacksmith-browser",
  },
};

const INJECTED_PROVIDER_IDS = new Set(
  Object.values(INJECTED_SERVERS).map((server) => server.providerId),
);

/**
 * True for codesmith-injected first-party servers ("Blacksmith Browser").
 * They have no saved server record and no org connection state; the
 * sidebar MCP widget renders them as always available.
 */
export function isInjectedMcpProvider(providerId: string | undefined): boolean {
  return providerId !== undefined && INJECTED_PROVIDER_IDS.has(providerId);
}

/** `s-aabbccdd_sentry-prod_list-issues` → server `sentry-prod`, tool `list-issues`. */
function parseSessionPrefixedToolName(
  toolName: string,
): { serverName: string; toolName: string } | null {
  const sessionMatch = toolName.match(MCP_SESSION_TOOL_PREFIX_RE);
  if (!sessionMatch) return null;
  const stripped = sessionMatch[1];
  const firstUnderscore = stripped.indexOf("_");
  if (firstUnderscore <= 0) return null;
  return {
    serverName: stripped.slice(0, firstUnderscore),
    toolName: stripped.slice(firstUnderscore + 1),
  };
}
const PROVIDER_BY_DISPLAY_PREFIX = new Map(
  MCP_PROVIDERS.map((provider) => [
    provider.displayName.replace(/[^a-zA-Z0-9_-]/g, "_"),
    provider,
  ]),
);

function fallbackProviderDisplayToolMetadata(
  toolName: string,
): McpToolMetadata | null {
  for (const [prefix, provider] of Array.from(PROVIDER_BY_DISPLAY_PREFIX)) {
    const toolPrefix = `${prefix}_`;
    if (!toolName.startsWith(toolPrefix)) continue;
    const mcpToolName = toolName.slice(toolPrefix.length);
    if (!mcpToolName) return null;
    return {
      serverName: provider.displayName,
      toolName: mcpToolName,
      providerId: provider.id,
    };
  }
  return null;
}

export interface McpToolMetadata {
  serverName: string;
  toolName: string;
  title?: string;
  // Slug for known integrations (e.g. "sentry", "linear", "datadog").
  // Set by the backend on the MCP server config and forwarded through
  // codesmith → opencode → state.metadata.mcpProviderId. The dashboard
  // uses this to look up the matching brand icon. May be inferred from
  // the server URL's host when the part carries no explicit id, so it is
  // display-only; use explicitProviderId as an identity signal.
  providerId?: string;
  // providerId exactly as recorded on the tool part (mcpProviderId),
  // never backfilled by host inference. A custom server hosted on a known
  // provider's domain gets an inferred providerId for its icon but no
  // explicitProviderId, so matching heuristics can't bind it to an
  // unrelated saved catalog server.
  explicitProviderId?: string;
  // Original MCP server URL. Forwarded through opencode as
  // state.metadata.mcpServerUrl so the dashboard can fall back to
  // host-based provider inference when no providerId is set.
  serverUrl?: string;
  // Backend saved-server key parsed from the `s-{session}_{key}_{tool}`
  // registration prefix (e.g. "axiom-prod-a1b2c3d4"). Keys are stable
  // across display-name edits, so this is the strongest identity signal
  // for binding a usage to a saved server record. Absent for injected
  // servers and legacy tool names without the session prefix.
  serverKey?: string;
}

/**
 * Read MCP-provided metadata for a tool part. Prefers structured metadata
 * forwarded from OpenCode via `state.metadata`. Falls back to parsing the
 * registration-name prefix when metadata isn't available. Returns null for
 * non-MCP tools.
 */
export function extractMcpToolMetadata(
  toolName: string,
  state?: Record<string, unknown>,
): McpToolMetadata | null {
  const metadata = state?.metadata as Record<string, unknown> | undefined;
  const metaServer = metadata?.mcpServerName;
  const metaTool = metadata?.mcpToolName;
  // Built-in system MCP server: its tools are presented with their own
  // built-in labels (e.g. "Created environment"), not "workspace-tools > ...".
  // Defensive guard against stale or regressed opencode metadata.
  if (metaServer === "workspace-tools") return null;
  if (
    typeof metaServer === "string" &&
    metaServer.length > 0 &&
    typeof metaTool === "string" &&
    metaTool.length > 0
  ) {
    const explicitId =
      typeof metadata?.mcpProviderId === "string" &&
      metadata.mcpProviderId.length > 0
        ? metadata.mcpProviderId
        : undefined;
    const serverUrl =
      typeof metadata?.mcpServerUrl === "string" &&
      metadata.mcpServerUrl.length > 0
        ? metadata.mcpServerUrl
        : undefined;
    const parsed = parseSessionPrefixedToolName(toolName);
    const injected = parsed ? INJECTED_SERVERS[parsed.serverName] : undefined;
    return {
      serverName: injected?.serverName ?? metaServer,
      toolName: metaTool,
      title:
        typeof metadata?.mcpToolTitle === "string"
          ? metadata.mcpToolTitle
          : undefined,
      providerId:
        explicitId ??
        injected?.providerId ??
        inferProviderFromHost(serverUrl ?? null) ??
        undefined,
      explicitProviderId: explicitId,
      serverUrl,
      serverKey: injected ? undefined : parsed?.serverName,
    };
  }

  // Fallback: derive server+tool from the registration-name prefix.
  const providerDisplayFallback = fallbackProviderDisplayToolMetadata(toolName);
  if (providerDisplayFallback) return providerDisplayFallback;

  const parsed = parseSessionPrefixedToolName(toolName);
  if (!parsed) return null;
  const injected = INJECTED_SERVERS[parsed.serverName];
  return {
    serverName: injected?.serverName ?? parsed.serverName,
    toolName: parsed.toolName,
    // Display-only brand icon hint; intentionally not explicitProviderId
    // (this is an inferred identity, not one recorded on the part).
    ...(injected
      ? { providerId: injected.providerId }
      : { serverKey: parsed.serverName }),
  };
}

/**
 * Build the display label for a tool call. When MCP metadata is present,
 * renders `{server} > {title|toolName}`. Otherwise delegates to the
 * provided fallback formatter for built-in tools.
 */
export function computeToolDisplayName(
  toolName: string,
  state: Record<string, unknown> | undefined,
  variant: "default" | "in-progress",
  fallback: (toolName: string) => string,
): string {
  const mcp = extractMcpToolMetadata(toolName, state);
  if (mcp) {
    const toolLabel =
      mcp.title ||
      mcp.toolName
        .replace(/[_-]+/g, " ")
        .replace(/([a-z])([A-Z])/g, "$1 $2")
        .replace(/\b\w/g, (c) => c.toUpperCase());
    const suffix = variant === "in-progress" ? "..." : "";
    return `${mcp.serverName} > ${toolLabel}${suffix}`;
  }
  return fallback(toolName);
}
